if (!customElements.get('pk-expandable-content')) {
    /**
     * <pk-expandable-content> Custom Element
     * Toggles the visibility of a content area based on a CSS max-height variable.
     *
     * Required markup inside <pk-expandable-content data-id="XYZ">:
     *   • An element with id="seo-content-XYZ" (the content to expand/collapse)
     *   • A toggle button with aria-controls="seo-content-XYZ"
     *     containing:
     *     – <span data-role="a11y"></span>
     *     – <span data-role="label"></span>
     *   • The toggle button must have data-show-more and data-show-less attributes for labels.
     *
     * Behavior:
     *   – If content.scrollHeight > CSS var(--max-height), shows the toggle button.
     *   – Clicking the toggle alternates between collapsed (max-height) and expanded (none).
     */
    class PkExpandableContent extends HTMLElement {
      constructor() {
        super();
  
        // DOM refs
        this.content = null;
        this.toggle = null;
        this.toggleA11y = null;
        this.toggleLabel = null;
  
        // State
        this.maxHeight = null;
        this.isExpanded = false;
        this.hasToggle = true;
  
        // Labels
        this.showMoreLabel = '';
        this.showLessLabel = '';
  
        this.handleExpandCollapse = this.handleExpandCollapse.bind(this);
      }
  
      connectedCallback() {
        try {
          this.init();
        } catch (error) {
          console.error('PkExpandableContent initialization error:', error);
        }
      }
  
      disconnectedCallback() {
        if (this.hasToggle && this.toggle) {
          this.toggle.removeEventListener('click', this.handleExpandCollapse);
        }
      }
  
      init() {
        const id = this.dataset.id;
        if (!id) {
          throw new Error('Missing required data-id attribute on <pk-expandable-content>.');
        }
  
        this.content = this.querySelector(`[id="seo-content-${id}"]`);
        this.toggle = this.querySelector(`[aria-controls="seo-content-${id}"]`);
        if (!this.content || !this.toggle) {
          throw new Error('Could not find content or toggle element based on id reference.');
        }
  
        this.toggleA11y = this.toggle.querySelector('[data-role="a11y"]');
        this.toggleLabel = this.toggle.querySelector('[data-role="label"]');
        if (!this.toggleA11y || !this.toggleLabel) {
          throw new Error('Missing required <span> elements with data-role attributes.');
        }
  
        this.maxHeight = this.extractMaxHeight();
  
        this.initButtonLabels();
        if (!this.setInitialState()) return;
  
        this.attachEventListeners();
      }
  
      initButtonLabels() {
        this.showMoreLabel = this.toggle.getAttribute('data-show-more')?.trim() || '';
        this.showLessLabel = this.toggle.getAttribute('data-show-less')?.trim() || '';
  
        if (!this.showMoreLabel || !this.showLessLabel) {
          throw new Error('Both data-show-more and data-show-less attributes are required on the toggle button.');
        }
      }
  
      /**
       * Set up initial collapsed/expanded state.
       * If content.scrollHeight > maxHeight, show the toggle. Otherwise, remove the toggle.
       * @returns {boolean} true if the toggle was shown; false if the toggle was removed.
       */
      setInitialState() {
        const isExpandedAttr = this.toggle.getAttribute('aria-expanded');
        this.isExpanded = isExpandedAttr === 'true';
  
        const contentHeight = this.content.scrollHeight;
        const shouldToggleBeVisible = contentHeight > this.maxHeight;
  
        if (shouldToggleBeVisible) {
          this.content.style.maxHeight = this.isExpanded ? 'none' : `${this.maxHeight}px`;
          this.safeSetToggleState(this.isExpanded ? this.showLessLabel : this.showMoreLabel);
          this.toggle.classList.add('pk-expandable-content__toggle--visible');
          this.content.classList.toggle('pk-expandable-content__item--gradient', !this.isExpanded);
          
          return true;
        }
  
        this.toggle.remove();
        this.hasToggle = false;
        this.content.style.maxHeight = 'none';
        this.content.classList.remove('pk-expandable-content__item--gradient');
        
        return false;
      }
  
      attachEventListeners() {
        if (this.hasToggle && this.toggle) {
          this.toggle.addEventListener('click', this.handleExpandCollapse);
        }
      }
  
      handleExpandCollapse() {
        if (!this.hasToggle) return;
  
        this.isExpanded = !this.isExpanded;
        this.content.style.maxHeight = this.isExpanded ? 'none' : `${this.maxHeight}px`;
        this.safeSetToggleState(this.isExpanded ? this.showLessLabel : this.showMoreLabel);
        this.content.classList.toggle('pk-expandable-content__item--gradient', !this.isExpanded);
      }
  
      /**
       * Safely update aria-expanded and button text if toggle exists.
       * @param {string} label - The label to set on the toggle spans.
       */
      safeSetToggleState(label) {
        if (this.hasToggle && this.toggle && this.toggleA11y && this.toggleLabel) {
          this.toggle.setAttribute('aria-expanded', this.isExpanded.toString());
          this.toggleA11y.textContent = label;
          this.toggleLabel.textContent = label;
        }
      }
  
      /**
       * Read the CSS variable "--max-height" from the content element.
       * @returns {number} The numeric value of --max-height in pixels.
       * @throws {Error} If the CSS variable is missing or not a positive number.
       */
      extractMaxHeight() {
        const cssValue = this.getCSSVariable(this.content, '--max-height');
        const numericValue = parseInt(cssValue, 10);
  
        if (!this.isPositiveNumber(numericValue)) {
          throw new Error(`Invalid --max-height CSS value: "${cssValue}"`);
        }
  
        return numericValue;
      }
  
      isPositiveNumber(value) {
        return typeof value === 'number' && !Number.isNaN(value) && value >= 0;
      }
  
      /**
       * Get a CSS variable’s value for a given element.
       * @param {HTMLElement} element
       * @param {string} variableName - e.g. "--max-height"
       * @returns {string} The raw CSS variable value (trimmed).
       */
      getCSSVariable(element, variableName) {
        return getComputedStyle(element).getPropertyValue(variableName).trim();
      }
    }
  
    customElements.define('pk-expandable-content', PkExpandableContent);
  }